import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { CheckGroup, DynRows, Field, YesNo } from "../components/fields";
import { AttachmentInput, PublicLinkButton } from "../components/FormLink";
import CountrySelect from "../components/CountrySelect";
import SignaturePad from "../components/SignaturePad";
import FormRecordsList from "../components/FormRecordsList";
import DateInput from "../components/DateInput";

const ADDR_KEYS = ["country", "province", "district", "corregimiento", "urbanizacion", "street", "building", "apt"];

const initial = {
  completed_by: [],
  legal_name: "", trade_name: "", ruc: "",
  addr_country: "", addr_province: "", addr_district: "", addr_corregimiento: "",
  addr_urbanizacion: "", addr_street: "", addr_building: "", addr_apt: "",
  const_country: "", const_date: "", operating_countries: "", activity: "",
  phone: "", email: "", website: "", tax_places: "", nit: "",
  directors: [], shareholders: [], legal_reps: [], resident_agent: [],
  is_pep: null, pep_position: "",
  is_pep_relative: null, pep_rel_name: "", pep_rel_position: "", pep_rel_relation: "",
  is_pep_collab: null, pep_col_name: "", pep_col_position: "", pep_col_relation: "",
  annual_income: "", other_income_desc: "", other_income_amount: "",
  client_name: "", date: new Date().toISOString().slice(0, 10), signature: null,
  broker_name: "", broker_date: "", broker_signature: null,
  reviewer_name: "", reviewer_date: "", reviewer_signature: null,
};

export default function FormAseguradoraPJ({ publicToken = null }) {
  const { t } = useLang();
  const locationEdit = useLocation().state?.edit || null;
  const [editDoc, setEditDoc] = useState(locationEdit);
  const [showForm, setShowForm] = useState(!!locationEdit);
  const [d, setD] = useState(() => (locationEdit ? { ...initial, ...locationEdit.data } : initial));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [sec, setSec] = useState(0);
  const [atts, setAtts] = useState([]);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setD({ ...d, [k]: e?.target ? e.target.value : e });
  const inp = (key, testid, type = "text") => (
    type === "date"
      ? <DateInput value={d[key]} onChange={set(key)} data-testid={testid} />
      : <input className="ec-input" type={type} value={d[key]} onChange={set(key)} data-testid={testid} />
  );

  const scrollToForm = () => setTimeout(() => document.querySelector('[data-testid="pj-form-wrap"]')?.scrollIntoView({ behavior: "smooth" }), 80);

  const newForm = () => {
    setEditDoc(null); setD(initial); setSaved(null); setSec(0); setShowForm(true); scrollToForm();
  };
  const startEdit = (doc) => {
    setEditDoc(doc); setD({ ...initial, ...doc.data }); setSaved(null); setSec(0); setShowForm(true); scrollToForm();
  };

  const PepBlock = ({ flag, fields, testid }) => (
    <div className="space-y-3" data-testid={testid}>
      <Field label={t(`a.${flag}`)}><YesNo value={d[flag]} onChange={(v) => setD({ ...d, [flag]: v })} testid={`pj-${flag}`} /></Field>
      {d[flag] && (
        <div className="grid sm:grid-cols-3 gap-4">
          {fields.map(([k, lbl]) => (
            <Field key={k} label={t(lbl)}>{inp(k, `pj-${k}`)}</Field>
          ))}
        </div>
      )}
    </div>
  );

  const SigCard = ({ title, nameKey, nameLabel, sigKey, dateKey, testid }) => (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid={testid}>
      <h3 className="font-bold text-sm text-slate-800">{title}</h3>
      <Field label={nameLabel}><input className="ec-input" value={d[nameKey]} onChange={set(nameKey)} data-testid={`${testid}-name`} /></Field>
      <Field label={t("common.date")}><DateInput className="ec-input" value={d[dateKey]} onChange={set(dateKey)} data-testid={`${testid}-date`} /></Field>
      <Field label={t("forms.signature")}><SignaturePad onChange={(v) => setD((p) => ({ ...p, [sigKey]: v }))} /></Field>
    </div>
  );

  const sections = [
    {
      title: t("a.sec0"), testid: "sec-completer",
      content: (
        <Field label={t("a.completed_by")}>
          <CheckGroup
            options={[t("a.cb_contractor"), t("a.cb_insured"), t("a.cb_broker"), t("a.cb_payer"), t("a.cb_insurer"), t("a.cb_beneficiary")]}
            values={d.completed_by}
            onChange={(v) => setD({ ...d, completed_by: v })}
            testidPrefix="pj-cb"
          />
        </Field>
      ),
    },
    {
      title: t("aj.sec1"), testid: "sec-general",
      content: (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label={t("aj.legal_name")}>{inp("legal_name", "pj-legal-name")}</Field>
            <Field label={t("aj.trade_name")}>{inp("trade_name", "pj-trade-name")}</Field>
            <Field label={t("aj.ruc")}>{inp("ruc", "pj-ruc")}</Field>
            <Field label={t("aj.const_country")}><CountrySelect value={d.const_country} onChange={(e) => setD((p) => ({ ...p, const_country: e.target.value }))} testid="pj-const-country" /></Field>
            <Field label={t("aj.const_date")}>{inp("const_date", "pj-const-date", "date")}</Field>
            <Field label={t("aj.operating_countries")}>{inp("operating_countries", "pj-operating-countries")}</Field>
            <Field label={t("aj.activity")} className="sm:col-span-2 lg:col-span-3">{inp("activity", "pj-activity")}</Field>
            <Field label={t("aj.phone")}>{inp("phone", "pj-phone")}</Field>
            <Field label={t("a.email")}>{inp("email", "pj-email", "email")}</Field>
            <Field label={t("aj.website")}>{inp("website", "pj-website")}</Field>
            <Field label={t("a.tax_places")} className="sm:col-span-2">{inp("tax_places", "pj-tax-places")}</Field>
            <Field label={t("a.nit")}>{inp("nit", "pj-nit")}</Field>
          </div>
          <div>
            <h3 className="font-bold text-sm text-slate-800 mb-3">{t("aj.addr_phys")}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pj-addr-phys">
              {ADDR_KEYS.map((k) => (
                <Field key={k} label={t(`a.addr_${k}`)}>
                  <input className="ec-input" value={d[`addr_${k}`]} onChange={set(`addr_${k}`)} data-testid={`pj-addr-${k}`} />
                </Field>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t("aj.sec2"), testid: "sec-directors",
      content: (
        <DynRows
          testid="pj-directors"
          columns={[
            { key: "name", label: t("aj.col_name") }, { key: "id_number", label: t("aj.col_id") },
            { key: "birth_date", label: t("aj.col_birth"), type: "date" }, { key: "address", label: t("aj.col_address") },
            { key: "position", label: t("aj.col_position") }, { key: "nationality", label: t("aj.col_nat") },
          ]}
          rows={d.directors}
          onChange={(v) => setD({ ...d, directors: v.slice(0, 5) })}
        />
      ),
    },
    {
      title: t("aj.sec3"), testid: "sec-shareholders",
      content: (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">{t("aj.sh_note")}</p>
          <DynRows
            testid="pj-shareholders"
            columns={[
              { key: "name", label: t("aj.col_name") }, { key: "id_number", label: t("aj.col_id") },
              { key: "birth_date", label: t("aj.col_birth"), type: "date" }, { key: "pct", label: t("aj.col_pct") },
              { key: "nationality", label: t("aj.col_nat") },
            ]}
            rows={d.shareholders}
            onChange={(v) => setD({ ...d, shareholders: v.slice(0, 5) })}
          />
        </div>
      ),
    },
    {
      title: t("aj.sec4"), testid: "sec-legal-rep",
      content: (
        <DynRows
          testid="pj-legal-reps"
          columns={[
            { key: "name", label: t("aj.col_name") }, { key: "id_number", label: t("aj.col_id") },
            { key: "birth_date", label: t("aj.col_birth"), type: "date" }, { key: "address", label: t("aj.col_address") },
          ]}
          rows={d.legal_reps}
          onChange={(v) => setD({ ...d, legal_reps: v.slice(0, 1) })}
        />
      ),
    },
    {
      title: t("aj.sec5"), testid: "sec-agent",
      content: (
        <DynRows
          testid="pj-resident-agent"
          columns={[{ key: "name", label: t("aj.col_name") }, { key: "address", label: t("aj.col_address") }]}
          rows={d.resident_agent}
          onChange={(v) => setD({ ...d, resident_agent: v.slice(0, 1) })}
        />
      ),
    },
    {
      title: t("a.sec2"), testid: "sec-pep",
      content: (
        <div className="space-y-6">
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="pj-pep-intro">{t("aj.pep_intro")}</p>
          <PepBlock flag="is_pep" fields={[["pep_position", "a.pep_position"]]} testid="pj-pep-self" />
          <PepBlock flag="is_pep_relative" fields={[["pep_rel_name", "a.pep_name"], ["pep_rel_position", "a.pep_rel_position"], ["pep_rel_relation", "a.pep_relation"]]} testid="pj-pep-relative" />
          <PepBlock flag="is_pep_collab" fields={[["pep_col_name", "a.pep_name"], ["pep_col_position", "a.pep_rel_position"], ["pep_col_relation", "a.pep_relation"]]} testid="pj-pep-collab" />
        </div>
      ),
    },
    {
      title: t("a.sec3"), testid: "sec-financial",
      content: (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("a.annual_income")}>
            <select className="ec-input" value={d.annual_income} onChange={set("annual_income")} data-testid="pj-annual-income">
              <option value="">—</option>
              {["r1", "r2", "r3", "r4", "r5", "r6", "r7"].map((k) => <option key={k} value={t(`aj.${k}`)}>{t(`aj.${k}`)}</option>)}
            </select>
          </Field>
          <Field label={t("a.other_income_desc")}>{inp("other_income_desc", "pj-other-income-desc")}</Field>
          <Field label={t("a.other_income_amount")}>{inp("other_income_amount", "pj-other-income-amount")}</Field>
        </div>
      ),
    },
    {
      title: t("a.sec5"), testid: "sec-consent",
      content: (
        <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 leading-relaxed" data-testid="pj-consent-text">
          {t("a.consent")}
        </p>
      ),
    },
    {
      title: t("a.sec6"), testid: "sec-signatures",
      content: (
        <div className="space-y-5">
          <div className="grid md:grid-cols-3 gap-5">
            <SigCard title={t("a.sig_client")} nameKey="client_name" nameLabel={t("a.sig_name")} sigKey="signature" dateKey="date" testid="pj-sig-client" />
            <SigCard title={t("a.sig_broker")} nameKey="broker_name" nameLabel={t("a.sig_name")} sigKey="broker_signature" dateKey="broker_date" testid="pj-sig-broker" />
            <SigCard title={t("a.sig_insurer")} nameKey="reviewer_name" nameLabel={t("a.sig_reviewer")} sigKey="reviewer_signature" dateKey="reviewer_date" testid="pj-sig-insurer" />
          </div>
          <p className="text-center text-xs text-slate-500 italic" data-testid="pj-ssrp-footer">{t("aj.footer")}</p>
        </div>
      ),
    },
    {
      title: t("a.sec7"), testid: "sec-docs",
      content: (
        <div className="space-y-4">
          <ul className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-1.5 list-disc list-inside" data-testid="pj-docs-text">
            <li>{t("aj.doc1")}</li>
            <li>{t("aj.doc2")}</li>
            <li>{t("aj.doc3")}</li>
            <li>{t("aj.doc4")}</li>
          </ul>
          <AttachmentInput value={atts} onChange={setAtts} testid="pj-attachments" />
        </div>
      ),
    },
  ];

  const submit = async () => {
    if (!d.signature) {
      toast.error(t("forms.sign_required"));
      setSec(9);
      return;
    }
    setBusy(true);
    try {
      if (publicToken) {
        const { data } = await api.post(`/public/form/${publicToken}`, { data: d, attachments: atts });
        setDone(data.form_number);
      } else if (editDoc) {
        await api.patch(`/forms/${editDoc.id}`, { data: d });
        setSaved({ id: editDoc.id, form_number: editDoc.form_number });
        toast.success(t("forms.updated"));
      } else {
        const { data } = await api.post("/forms", { form_type: "dd_pj_aseguradora", data: d, attachments: atts });
        setSaved(data);
        toast.success(`${t("forms.save_success")} — ${data.form_number}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="form-public-done">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="form-aseguradora-pj-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("aj.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("aj.subtitle")}</p>
        </div>
        {!publicToken && (
          <div className="flex items-center gap-2">
            <button onClick={newForm} data-testid="pj-create-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
              <Plus className="w-4 h-4" /> {t("frl.new")}
            </button>
            <PublicLinkButton formType="dd_pj_aseguradora" testid="pj-public-link" />
          </div>
        )}
      </div>

      {!publicToken && <FormRecordsList formType="dd_pj_aseguradora" onEdit={startEdit} refresh={saved} testid="pj" />}

      {saved && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="form-saved-banner">
          <span className="text-sm text-emerald-800 font-semibold">{t("forms.doc_no")} <span className="font-mono">{saved.form_number}</span></span>
          <button onClick={() => downloadFile(`/forms/${saved.id}/pdf`, `${saved.form_number}.pdf`)} data-testid="download-form-pdf" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C]">
            <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
          </button>
          <button onClick={() => { setD(initial); setEditDoc(null); setSaved(null); setSec(0); }} data-testid="new-form-button" className="text-xs font-bold text-slate-600 hover:text-slate-900 underline">
            {t("n.rt_new")}
          </button>
        </div>
      )}

      {(publicToken || showForm) && (
        <div className="space-y-6" data-testid="pj-form-wrap">
          <div className="space-y-1.5" data-testid="pj-progress-wrap">
            <div className="flex justify-between text-[11px] font-semibold text-[#8A94A2]">
              <span>{sections[sec].title}</span>
              <span data-testid="pj-progress-pct">{Math.round(((sec + 1) / sections.length) * 100)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#EFF4FC] overflow-hidden">
              <div className="h-full rounded-full bg-[#044CBC] transition-all duration-500" style={{ width: `${((sec + 1) / sections.length) * 100}%` }} data-testid="pj-progress" />
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {sections.map((s, i) => (
              <button key={i} onClick={() => setSec(i)} data-testid={`pj-tab-${i}`} className={`shrink-0 px-3.5 py-2 rounded-lg text-xs font-bold transition-colors ${sec === i ? "bg-[#16335F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                {s.title}
              </button>
            ))}
          </div>

          <div className="ec-card p-6" data-testid={`pj-section-${sections[sec].testid}`}>
            <h2 className="font-heading font-bold text-slate-900 mb-5">{sections[sec].title}</h2>
            {sections[sec].content}
            <div className="flex justify-between mt-8">
              <button onClick={() => setSec(Math.max(0, sec - 1))} disabled={sec === 0} data-testid="pj-prev" className="px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-30">←</button>
              {sec < sections.length - 1 ? (
                <button onClick={() => setSec(sec + 1)} data-testid="pj-next" className="px-6 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C]">→</button>
              ) : (
                <button onClick={submit} disabled={busy} data-testid="pj-submit" className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {busy ? t("common.saving") : t("common.save")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
